import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useDialog } from '@/lib/hooks/use-dialog'
import { type ResMember } from '@/lib/queries/business/v1/types'
import { inviteMemberMutation, removeInvitedMemberMutation } from '@/lib/queries/business/v1/user/invite/mutations'

export const useToolsMenu = () => {
  const { mutateAsync: inviteMember } = useMutation(inviteMemberMutation)
  const { mutateAsync: removeInvitedMember, isPending: isPendingRemove } = useMutation(removeInvitedMemberMutation)

  const {
    isDialogOpen: isOpenRemoveConfirmDialog,
    openDialog: openRemoveConfirmDialog,
    closeDialog: closeRemoveConfirmDialog,
  } = useDialog()

  async function handleClickResendMenuItem(memberData: ResMember) {
    await inviteMember(
      {
        users: [
          {
            email: memberData.email,
            role: memberData.role,
          },
        ],
      },
      {
        onSuccess: () => {
          toast.success('Invite email sent again!')
        },
        onError: () => {
          toast.error('Resend failed. Please try again later.')
        },
      }
    )
  }

  function handleClickRemoveMenuItem() {
    openRemoveConfirmDialog()
  }

  function handleChangeRemoveConfigDialogOpen(open: boolean) {
    if (open) {
      return
    }

    closeRemoveConfirmDialog()
  }

  function handleClickCancelButton() {
    closeRemoveConfirmDialog()
  }

  async function handleClickRemoveButton(memberData: ResMember) {
    await removeInvitedMember(
      { users: [{ email: memberData.email }] },
      {
        onSuccess: () => {
          setTimeout(async () => {
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v1.user.members.pending.index],
            })
          }, 1000)
          toast.success(`Remove ${memberData.email} successfully!`)
          closeRemoveConfirmDialog()
        },
        onError: () => {
          toast.error('Remove failed. Please try again later.')
        },
      }
    )
  }

  return {
    isOpenRemoveConfirmDialog,
    isPendingRemove,
    handleClickResendMenuItem,
    handleClickRemoveMenuItem,
    handleChangeRemoveConfigDialogOpen,
    handleClickCancelButton,
    handleClickRemoveButton,
  }
}
